import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { analyticsService, AnalyticsEvent } from '@cobu/analytics';
import { RootState } from '../store';
import { useUser } from './userHooks';
import { logger } from '../utils/logger';
import { showErrorMessage, showSuccessMessage } from '../utils/messageToast';

interface SkillData {
  firstSkill: string;
  secondSkill: string;
  thirdSkill: string;
}

export function useManagePublicProfile() {
  const navigate = useNavigate();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const {
    updateUser,
    uploadProfileImage,
    isUpdateUserPending,
    isUploadProfileImagePending,
  } = useUser();

  // For web, we can derive "public profile" data directly from the authenticated user
  const profileData = currentUser ? { user: currentUser } : null;
  const isProfileLoading = false;
  const profileError = null;

  // State
  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const [currentImageFile, setCurrentImageFile] = useState<File | null>(null);
  const [about, setAbout] = useState('');
  const [likesHelpingWith, setLikesHelpingWith] = useState<SkillData>({
    firstSkill: '',
    secondSkill: '',
    thirdSkill: '',
  });
  const [needsHelpWith, setNeedsHelpWith] = useState<SkillData>({
    firstSkill: '',
    secondSkill: '',
    thirdSkill: '',
  });
  const [changedPicture, setChangedPicture] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize data from profile ONCE when it first loads
  useEffect(() => {
    if (profileData?.user && !isInitialized) {
      setCurrentImage(profileData.user.profileImage || null);
      setAbout(profileData.user.bio || '');
      
      // Initialize likesHelpingWith with existing data
      if (profileData.user.likesHelpingWith && Array.isArray(profileData.user.likesHelpingWith)) {
        setLikesHelpingWith({
          firstSkill: profileData.user.likesHelpingWith[0] || '',
          secondSkill: profileData.user.likesHelpingWith[1] || '',
          thirdSkill: profileData.user.likesHelpingWith[2] || '',
        });
      }
      
      // Initialize needsHelpWith with existing data
      if (profileData.user.needsHelpWith && Array.isArray(profileData.user.needsHelpWith)) {
        setNeedsHelpWith({
          firstSkill: profileData.user.needsHelpWith[0] || '',
          secondSkill: profileData.user.needsHelpWith[1] || '',
          thirdSkill: profileData.user.needsHelpWith[2] || '',
        });
      }
      
      setIsInitialized(true);
    }
  }, [profileData, isInitialized]);

  // Image picker handlers
  const handleImageSelected = useCallback((imageUrl: string, file: File) => {
    setCurrentImage(imageUrl);
    setCurrentImageFile(file);
    setChangedPicture(true);
  }, []);

  const handleShowImagePicker = useCallback(() => {
    /* ANALYTICS_DISABLED: PRESS_IMAGE_UPLOAD_BUTTON ('press_image_upload_button')
      analyticsService.trackEvent(AnalyticsEvent.PRESS_IMAGE_UPLOAD_BUTTON);
      */
    setShowImagePicker(true);
  }, []);

  const handleHideImagePicker = useCallback(() => {
    setShowImagePicker(false);
  }, []);

  // Save handler
  const handleSave = useCallback(async () => {
    if (!currentUser?.uuid) {
      showErrorMessage('User not found');
      return;
    }

    try {
      // Upload profile image if changed
      if (changedPicture && currentImageFile) {
        await uploadProfileImage(currentUser.uuid, currentImageFile);
      }

      // Update user data
      const updatedUserData = {
        bio: about.trim(),
        likesHelpingWith: [
          likesHelpingWith.firstSkill.trim(),
          likesHelpingWith.secondSkill.trim(),
          likesHelpingWith.thirdSkill.trim(),
        ].filter(Boolean), // Remove empty strings
        needsHelpWith: [
          needsHelpWith.firstSkill.trim(),
          needsHelpWith.secondSkill.trim(),
          needsHelpWith.thirdSkill.trim(),
        ].filter(Boolean), // Remove empty strings
      };
      /* ANALYTICS_DISABLED: UPDATE_PROFILE_INFO ('update_profile_info')
        analyticsService.trackEvent(AnalyticsEvent.UPDATE_PROFILE_INFO);
        */

      await updateUser(currentUser.uuid, updatedUserData);


      showSuccessMessage('Profile updated successfully');
      navigate(-1);
    } catch (error) {
      logger.error('Save profile error:', error);
      showErrorMessage('Failed to update profile', 'Please try again.');
    }
  }, [
    currentUser?.uuid,
    currentImageFile,
    changedPicture,
    about,
    likesHelpingWith,
    needsHelpWith,
    uploadProfileImage,
    updateUser,
    navigate,
  ]);

  // Skill input handlers
  const handleLikesHelpingWithChange = useCallback((field: keyof SkillData, value: string) => {
    setLikesHelpingWith(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleNeedsHelpWithChange = useCallback((field: keyof SkillData, value: string) => {
    setNeedsHelpWith(prev => ({ ...prev, [field]: value }));
  }, []);

  return {
    // Data
    profileData,
    isProfileLoading,
    profileError,
    currentUser,
    currentImage,
    about,
    likesHelpingWith,
    needsHelpWith,
    showImagePicker,
    isLoading: isUpdateUserPending || isUploadProfileImagePending,
    
    // Handlers
    handleSave,
    handleImageSelected,
    handleShowImagePicker,
    handleHideImagePicker,
    handleLikesHelpingWithChange,
    handleNeedsHelpWithChange,
    setAbout,
  };
} 